// Porte de v_leads_parados e v_lead_score
// (supabase/migrations/20260903100300_v11_views_parados_score.sql, mantido
// no historico do git) para JS. Mesmo raciocinio de dashboard.ts: sem view
// no Firestore, busca as linhas de origem e replica a MESMA formula, em vez
// de reinventar. Viavel no volume de dados de uma prospeccao de 2-3 pessoas.
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import type { Lead, LeadScore, LeadScoreFaixa, VLeadParado } from "./types";

async function fetchLeadsAtivosParaInsight(): Promise<Lead[]> {
  const r = await getDocs(collection(db, "leads"));
  return r.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Lead, "id">) }))
    .filter((l) => !l.arquivado_em && !l.nao_contatar && !["cliente_fechado", "recusou", "nao_fechou"].includes(l.etapa));
}

async function fetchDataEntradaEtapa(): Promise<Map<string, string>> {
  const r = await getDocs(collection(db, "lead_stage_events"));
  const maisRecente = new Map<string, string>();
  for (const d of r.docs) {
    const e = d.data() as { lead_id: string; ocorreu_em: string };
    const atual = maisRecente.get(e.lead_id);
    if (!atual || e.ocorreu_em > atual) maisRecente.set(e.lead_id, e.ocorreu_em);
  }
  return maisRecente;
}

/** v_leads_parados NAO pre-filtra por dias: o front escolhe o corte. Leads
 * adiados para o futuro já saem daqui (mesma regra da view original). */
export async function fetchLeadsParados(minDias: number): Promise<VLeadParado[]> {
  const [leads, entradaEtapa] = await Promise.all([fetchLeadsAtivosParaInsight(), fetchDataEntradaEtapa()]);
  const agora = Date.now();

  const linhas: VLeadParado[] = [];
  for (const l of leads) {
    // adiado para o futuro nao e esquecido
    if (l.proximo_contato_em && new Date(l.proximo_contato_em).getTime() > agora) continue;

    const entrouNaEtapaEm = entradaEtapa.get(l.id) ?? l.criado_em;
    const ultimaMovimentacaoEm =
      l.ultima_tentativa_em && l.ultima_tentativa_em > entrouNaEtapaEm ? l.ultima_tentativa_em : entrouNaEtapaEm;

    const diasParado = Math.floor((agora - new Date(ultimaMovimentacaoEm).getTime()) / 86_400_000);
    const diasNaEtapa = Math.floor((agora - new Date(entrouNaEtapaEm).getTime()) / 86_400_000);
    const diasAtrasado = l.proximo_contato_em
      ? Math.floor((agora - new Date(l.proximo_contato_em).getTime()) / 86_400_000)
      : null;

    if (diasParado < minDias) continue;

    linhas.push({
      lead_id: l.id,
      empresa: l.empresa,
      cidade: l.cidade,
      ramo: l.ramo,
      etapa: l.etapa,
      prioridade: l.prioridade,
      responsavel_id: l.responsavel_id,
      proximo_contato_em: l.proximo_contato_em,
      ultima_tentativa_em: l.ultima_tentativa_em,
      tentativas_total: l.tentativas_total,
      tentativas_sem_resposta: l.tentativas_sem_resposta,
      entrou_na_etapa_em: entrouNaEtapaEm,
      ultima_movimentacao_em: ultimaMovimentacaoEm,
      dias_parado: diasParado,
      dias_na_etapa: diasNaEtapa,
      dias_atrasado: diasAtrasado,
      parado_default: diasParado >= 7,
    });
  }
  return linhas.sort((a, b) => b.dias_parado - a.dias_parado);
}

// -----------------------------------------------------------------------------
// Score por sinal individual — HEURISTICA DECLARADA, nao aprendida. Ver o
// comentario completo na migration original: os pesos abaixo sao os MESMOS,
// num bloco so, editaveis quando a pratica pedir (nao antes).
// -----------------------------------------------------------------------------
function calcularScoreLead(l: Lead): { score: number; faixa: LeadScoreFaixa; componentes: string[] } {
  const agora = Date.now();
  const componentes: string[] = [];
  let score = 0;

  if (l.telefone) { score += 20; componentes.push("tem telefone (+20)"); }
  if (l.pessoa_contato) { score += 10; componentes.push("tem pessoa de contato (+10)"); }
  if (l.email) { score += 8; componentes.push("tem e-mail (+8)"); }
  if (l.site) { score += 4; componentes.push("tem site (+4)"); }

  if (l.ultima_resposta_em) { score += 25; componentes.push("já respondeu alguma vez (+25)"); }
  if (l.etapa === "reuniao_agendada" || l.etapa === "reuniao_realizada") {
    score += 15;
    componentes.push("reunião agendada/realizada (+15)");
  }

  const diasDesdeInteracao = (agora - new Date(l.data_ultima_interacao).getTime()) / 86_400_000;
  if (diasDesdeInteracao <= 7) { score += 10; componentes.push("interagiu nos últimos 7 dias (+10)"); }
  else if (diasDesdeInteracao <= 30) { score += 5; componentes.push("interagiu nos últimos 30 dias (+5)"); }
  else if (diasDesdeInteracao > 90) { score -= 10; componentes.push("sem interação há mais de 90 dias (-10)"); }

  if (l.tentativas_sem_resposta >= 5) { score -= 25; componentes.push("5+ tentativas sem resposta (-25)"); }
  else if (l.tentativas_sem_resposta >= 3) { score -= 15; componentes.push("3+ tentativas sem resposta (-15)"); }

  const diasDesdeCriacao = (agora - new Date(l.criado_em).getTime()) / 86_400_000;
  if (l.tentativas_total === 0 && diasDesdeCriacao > 30) {
    score -= 5;
    componentes.push("cadastrado há 30+ dias e nunca tentado (-5)");
  }

  if (l.prioridade === "alta") { score += 10; componentes.push("prioridade alta (+10)"); }
  else if (l.prioridade === "baixa") { score -= 10; componentes.push("prioridade baixa (-10)"); }

  score = Math.max(0, Math.min(100, score));
  const faixa: LeadScoreFaixa = score >= 70 ? "quente" : score >= 40 ? "morno" : "frio";

  return { score, faixa, componentes };
}

export async function fetchLeadScores(): Promise<LeadScore[]> {
  const leads = await fetchLeadsAtivosParaInsight();
  return leads.map((l) => {
    const { score, faixa, componentes } = calcularScoreLead(l);
    return {
      lead_id: l.id,
      empresa: l.empresa,
      cidade: l.cidade,
      ramo: l.ramo,
      etapa: l.etapa,
      prioridade: l.prioridade,
      responsavel_id: l.responsavel_id,
      tentativas_total: l.tentativas_total,
      tentativas_sem_resposta: l.tentativas_sem_resposta,
      data_ultima_interacao: l.data_ultima_interacao,
      score,
      faixa,
      componentes,
    };
  });
}

export async function fetchLeadScore(leadId: string): Promise<LeadScore | null> {
  const todos = await fetchLeadScores();
  return todos.find((s) => s.lead_id === leadId) ?? null;
}
